import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type Ref } from 'react';

import { Sheen } from '@/components/atoms';

type Props = {
  eyebrow: string;
  title: string;
  text: string;
  /** Target of the enclosing Section's `aria-labelledby`. */
  titleId: string;
  /**
   * The element whose arrival triggers the sheen. It is this block and not
   * the heading itself: the observer should fire when the *band* has been
   * reached, which is a little before the heading alone crosses the margin.
   */
  sheenRef: Ref<HTMLDivElement>;
  playSheen: boolean;
};

/**
 * The promise in words: the eyebrow that sets up the surprise, the headline
 * the light crosses, and the sentence that explains what importing to order
 * actually means for the reader.
 *
 * `Sheen` wraps the headline alone. Sweeping the whole block would run the
 * light across body copy, where it reads as a selection highlight rather than
 * as emphasis, and would make the pass as wide as the paragraph is tall.
 */
export function ImportPromiseCopy({
  eyebrow,
  title,
  text,
  titleId,
  sheenRef,
  playSheen,
}: Props) {
  return (
    <Stack ref={sheenRef} spacing={4} sx={{ alignItems: 'flex-start', textAlign: 'left' }}>
      <Typography
        variant="overline"
        sx={{
          px: 4,
          py: 1,
          borderRadius: 999,
          color: 'primary.main',
          bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.1)',
          border: 1,
          borderColor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.24)',
        }}
      >
        {eyebrow}
      </Typography>

      {/*
        The sweep needs a little room either side of the glyphs, or the light
        appears to start inside the first letter. Negative margin puts that
        room back so the heading still measures from the block's own edge.
      */}
      <Sheen play={playSheen} sx={{ px: 4, mx: -4, borderRadius: 2 }}>
        <Typography
          id={titleId}
          variant="h2"
          component="h2"
          sx={{ maxWidth: '14ch', textWrap: 'balance' }}
        >
          {title}
        </Typography>
      </Sheen>

      <Typography variant="subtitle1" sx={{ maxWidth: '48ch', color: 'text.secondary' }}>
        {text}
      </Typography>
    </Stack>
  );
}
